import { Menu } from "lucide-react";
import { Avatar } from "./avatar";
import { CopyButton } from "./copy-button";
import { humanizeState, truncateId } from "./format";
import { Notifications, type NotificationItem } from "./notifications";
import { UiLink } from "./router";
import { StatusPill } from "./status-pill";
import { ThemeToggle } from "./theme-toggle";

export function TopBar({
  initials,
  suiteId,
  registrationState,
  registrationTone = "success",
  notifications,
  onMenu,
}: {
  initials: string;
  suiteId: string;
  registrationState: string;
  /**
   * The design hard-coded `success`, because its fixture account was always
   * registered. A real account can sit in `pending_phone_verification`, and a
   * green pill on a blocked account is a lie — so the screen that knows the
   * state picks the tone.
   */
  registrationTone?: "success" | "warning" | "danger" | "brand" | "neutral";
  notifications: NotificationItem[];
  onMenu: () => void;
}) {
  return (
    <header className="sticky top-0 z-20 border-b border-line bg-surface/85 backdrop-blur-md supports-[backdrop-filter]:bg-surface/70">
      <div className="flex h-topbar items-center gap-3 px-4 sm:px-6">
        <button
          type="button"
          onClick={onMenu}
          aria-label="Open navigation"
          className="grid size-9 shrink-0 place-items-center rounded-field text-ink-2 hover:bg-surface-sunken lg:hidden"
        >
          <Menu className="size-4.5" aria-hidden />
        </button>

        {/* Suite id — the tenant you are looking at */}
        <div className="hidden items-center gap-1.5 rounded-field bg-surface-sunken py-1.5 pr-1.5 pl-3 md:flex">
          <span className="text-2xs font-semibold text-ink-4">Suite</span>
          <code className="font-mono text-2xs text-ink-3">{truncateId(suiteId)}</code>
          <CopyButton value={suiteId} label="Copy suite id" />
        </div>

        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <StatusPill tone={registrationTone} dot pulse className="hidden sm:inline-flex">
            {humanizeState(registrationState)}
          </StatusPill>

          <ThemeToggle className="hidden sm:inline-flex" />

          <Notifications items={notifications} />

          <UiLink
            href="/security"
            aria-label="Your profile"
            className="rounded-full transition-transform hover:scale-105"
          >
            <Avatar initials={initials} size="sm" />
          </UiLink>
        </div>
      </div>
    </header>
  );
}
